import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Stack,
} from "@mui/material";
import { Add, Warning } from "@mui/icons-material";
import {
  createSite,
  restoreSite,
  allSites,
  deleteSiteById,
  cloneSite,
} from "../redux/slices/siteSlice";
import { createNewBackup } from "../redux/slices/backupSlice";
import {
  useVerificationGuard,
  VERIFY_MSG,
} from "../hooks/useVerificationGuard";
import CreateSiteDialog from "../components/dialog/CreateSiteDialog";
import { EmptyState } from "../components/common";
import { SitesList, SiteFilters, SiteActionMenu } from "../components/sites";
import { useMessage, useFilters } from "../hooks/useCommon";
import { useSiteManagement } from "../hooks/useSiteManagement";
import { SPACING, ICON_SIZES } from "../theme/constants";
import { Public } from "@mui/icons-material";

const Sites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management with custom hooks
  const { message, messageType, showSuccess, showError } = useMessage();
  const { filters, updateFilter } = useFilters({
    status: "",
    environment: "",
    search: "",
    showDeleted: false,
  });

  // Component state
  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_siteCreation, setSiteCreation] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [_backupLoading, setBackupLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneEnvironment, setCloneEnvironment] = useState("SANDBOX");
  // eslint-disable-next-line no-unused-vars
  const [_siteCreationStatus, setSiteCreationStatus] = useState("");
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [newSite, setNewSite] = useState({
    name: "",
    environment: "SANDBOX",
  });

  // Redux state
  const { site } = useSelector((state) => state.site);
  const isVerifiedOrAdmin = useVerificationGuard();

  // Socket management and data fetching
  useSiteManagement({
    filters,
    page: currentPage,
    setSiteCreationStatus,
    setSiteCreation,
    showSuccess,
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search,
    filters.status,
    filters.environment,
    filters.showDeleted,
  ]);

  // Dialog handlers
  const handleOpenDialog = () => {
    if (!isVerifiedOrAdmin) {
      showError(VERIFY_MSG);
      return;
    }
    setOpenDialog(true);
    setNewSite({ name: "", environment: "SANDBOX" });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleCreateSite = async () => {
    if (!isVerifiedOrAdmin) {
      showError(VERIFY_MSG);
      return;
    }
    setSiteCreation(true);
    handleCloseDialog();
    try {
      await dispatch(createSite(newSite)).unwrap();
      showSuccess("Site creation started!");
      setTimeout(() => dispatch(allSites()), 1000);
    } catch (error) {
      showError(error?.message || error?.error || "Failed to create site");
      setSiteCreation(false);
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, site) => {
    setAnchorEl(event.currentTarget);
    setSelectedSite(site);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Site action handlers
  const handleRestoreSite = async (siteId) => {
    if (!isVerifiedOrAdmin) {
      showError(VERIFY_MSG);
      return;
    }
    setRestoreLoading(true);
    try {
      await dispatch(restoreSite(siteId)).unwrap();
      showSuccess("Site restored successfully!");
      setTimeout(() => {
        dispatch(
          allSites({
            environment: filters.environment || "",
            status: filters.status || "",
            search: filters.search || "",
            includeDeleted: filters.showDeleted,
          }),
        );
      }, 1000);
    } catch (error) {
      showError(`Failed to restore site: ${error.error || error.message}`);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleManageSite = (siteId) => {
    navigate(`/dashboard/sites/${siteId}`);
  };

  const siteProtocol =
    process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http";

  const handleVisitSite = (domain) => {
    window.open(`${siteProtocol}://${domain}/`, "_blank");
  };

  const handleBackupSite = async (site) => {
    if (!isVerifiedOrAdmin) {
      showError(VERIFY_MSG);
      return;
    }
    setBackupLoading(true);
    try {
      await dispatch(
        createNewBackup({
          siteId: site.id,
          payload: {
            backupType: "MANUAL",
            backupName: `Quick backup - ${new Date().toLocaleString()}`,
            notes: "Created from site card menu",
          },
        }),
      ).unwrap();
      showSuccess(`Backup created for ${site.name}`);
    } catch (error) {
      showError(`Failed to create backup: ${error.message || error}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCloneSite = (site) => {
    if (!isVerifiedOrAdmin) {
      showError(VERIFY_MSG);
      return;
    }
    setSelectedSite(site);
    setCloneName(`${site.name}-clone`);
    setCloneEnvironment(site.environment || "SANDBOX");
    setCloneDialogOpen(true);
  };

  const handleConfirmClone = async () => {
    if (!selectedSite || !cloneName) return;
    setCloneLoading(true);
    try {
      await dispatch(
        cloneSite({
          siteId: selectedSite.id,
          payload: {
            newName: cloneName,
            targetEnvironment: cloneEnvironment,
          },
        }),
      ).unwrap();
      showSuccess(`Site cloned as ${cloneName}`);
      setCloneDialogOpen(false);
      dispatch(allSites(filters));
    } catch (error) {
      showError(`Failed to clone site: ${error.message || error}`);
    } finally {
      setCloneLoading(false);
    }
  };

  const handleSiteSettings = (site) => {
    navigate(`/dashboard/sites/${site.id}?tab=settings`);
  };

  const handleDeleteSite = (site) => {
    if (!isVerifiedOrAdmin) {
      showError(VERIFY_MSG);
      return;
    }
    setSelectedSite(site);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedSite) return;
    setDeleteLoading(true);
    try {
      await dispatch(deleteSiteById(selectedSite.id)).unwrap();
      showSuccess(`Site ${selectedSite.name} deleted successfully`);
      setDeleteDialogOpen(false);
      dispatch(allSites(filters));
    } catch (error) {
      showError(
        `Failed to delete site: ${error?.error || error?.message || (typeof error === "string" ? error : "Unknown error")}`,
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const sites = site?.sites || [];
  const loading = site?.loading || false;

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: SPACING.lg,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Your WordPress Sites
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage and monitor all your WordPress installations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
          disableElevation
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          Create New Site
        </Button>
      </Box>

      {/* Success / Error Message */}
      {message && (
        <Alert severity={messageType} sx={{ mb: SPACING.md, borderRadius: 2 }}>
          {message}
        </Alert>
      )}

      {/* Filters and Search */}
      <SiteFilters
        searchQuery={filters.search}
        onSearchChange={(value) => updateFilter("search", value)}
        filterStatus={filters.status}
        onStatusChange={(value) => updateFilter("status", value)}
        filterEnvironment={filters.environment}
        onEnvironmentChange={(value) => updateFilter("environment", value)}
        showDeleted={filters.showDeleted}
        onShowDeletedChange={(value) => updateFilter("showDeleted", value)}
        totalCount={site?.pagination?.totalCount || 0}
        visibleCount={sites.length}
      />

      {/* Sites List */}
      <SitesList
        sites={sites}
        loading={loading}
        onMenuOpen={handleMenuOpen}
        onManage={handleManageSite}
        onVisit={handleVisitSite}
        onRestore={handleRestoreSite}
        restoreLoading={restoreLoading}
      />

      {/* Pagination */}
      {(site?.pagination?.totalPages || 0) > 1 && (
        <Stack alignItems="center" sx={{ mt: SPACING.md, mb: SPACING.md }}>
          <Pagination
            count={site.pagination.totalPages}
            page={currentPage}
            onChange={(e, page) => setCurrentPage(page)}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}

      {/* Empty State */}
      {!loading && sites.length === 0 && (
        <EmptyState
          icon={<Public sx={{ fontSize: ICON_SIZES.xxl }} />}
          title="No sites found"
          description={
            filters.search
              ? "Try adjusting your search or filters"
              : "Get started by creating your first WordPress site"
          }
          action={
            !filters.search && {
              label: "Create New Site",
              icon: <Add />,
              onClick: handleOpenDialog,
            }
          }
        />
      )}

      {/* Site Action Menu */}
      <SiteActionMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        selectedSite={selectedSite}
        onBackup={handleBackupSite}
        onClone={handleCloneSite}
        onSettings={handleSiteSettings}
        onDelete={handleDeleteSite}
      />

      {/* Create Site Dialog */}
      <CreateSiteDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onCreate={handleCreateSite}
        newSite={newSite}
        setNewSite={setNewSite}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Warning color="error" />
          Delete Site
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedSite?.name}</strong>? This action will stop all
            containers and mark the site as deleted.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You can restore the site later from the deleted sites view.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {deleteLoading ? <CircularProgress size={20} /> : "Delete Site"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Site Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={() => setCloneDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Site</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Create a copy of <strong>{selectedSite?.name}</strong> with a new
            name.
          </Typography>
          <TextField
            fullWidth
            label="New Site Name"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            helperText="Enter a unique name for the cloned site"
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>Target Environment</InputLabel>
            <Select
              value={cloneEnvironment}
              label="Target Environment"
              onChange={(e) => setCloneEnvironment(e.target.value)}
            >
              <MenuItem value="SANDBOX">Sandbox</MenuItem>
              <MenuItem value="STAGING">Staging</MenuItem>
              <MenuItem value="PRODUCTION">Production</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setCloneDialogOpen(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmClone}
            disabled={cloneLoading || !cloneName}
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            {cloneLoading ? <CircularProgress size={20} /> : "Clone Site"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Sites;
